import { useEffect, useState, useCallback } from "react";
import LoadingInComponent from "../../Components/LoadingInComponent";
import axios from "axios";
import {
  DELETE_TAG_API,
  GET_GUESTS_TAG_API,
  GET_TAG_DETAILS_API,
  UPDATE_TAG_API,
} from "../../Constant/api";
import { useToast } from "../../Components/Toast";
import Breadcrumb from "../../Components/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import TimestampCard from "../../Components/TimestampCard";
import TextboxSet from "../../Components/TextboxSet";
import SectionCard from "../../Components/SectionCard";
import Button from "../../Components/Button";
import { TAG_MANAGEMENT } from "../../Router/path";
import Modal from "../../Components/Modal";
import { Warning2 } from "iconsax-react";
import TagEditTable from "../../Components/Tables/TagEditTable";
import Can from "../../Components/Can";

const TagsEdit = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    color: "#5C4BF5",
  });
  const [timeStampData, setTimestampData] = useState({
    created_at: "",
    updated_at: "",
  });
  const [originalName, setOriginalName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [tableData, setTableData] = useState(null);
  const [tableMeta, setTableMeta] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchCleared, setIsSearchCleared] = useState(false);
  const [perPage] = useState(10);

  const { toast } = useToast();
  const { state } = useLocation();
  const navigate = useNavigate();

  const breadcrumbItems = [
    {
      label: "Tag Management",
      to: TAG_MANAGEMENT,
    },
    { label: originalName },
    { label: "Edit" },
  ];

  const fetchTagDetails = useCallback(() => {
    setLoading(true);
    axios
      .get(GET_TAG_DETAILS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          tag_id: state,
        },
      })
      .then((response) => {
        setFormData({
          name: response.data.data.name,
          color: response.data.data.color,
        });
        setTimestampData({
          created_at: response.data.data.created_at,
          updated_at: response.data.data.updated_at,
        });
        setOriginalName(response.data.data.name);
        setLoading(false);
      })
      .catch((error) => {
        toast("Failed to fetch tag details", "error");
        console.error(error);
        setLoading(false);
      });
  }, [state, toast]);

  const handleUpdateTag = () => {
    setLoading(true);
    axios
      .put(
        UPDATE_TAG_API,
        {
          tag_id: state,
          name: formData.name,
          color: formData.color,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        }
      )
      .then((res) => {
        toast("Tag updated successfully", "success");
        setLoading(false);
        navigate(TAG_MANAGEMENT);
        // fetchTagDetails();
      })
      .catch((err) => {
        toast("Failed to update tag", "error");
        console.error(err);
        setLoading(false);
      });
  };

  const fetchGuestTagTableData = useCallback((page = 1) => {
    setLoading(true);
    axios
      .get(GET_GUESTS_TAG_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          tag_id: state,
          page: page,
          perPage: perPage,
          search: searchTerm,
        },
      })
      .then((response) => {
        setTableData(response.data.data);
        setTableMeta(response.data.meta.pagination);
        setLoading(false);
      })
      .catch((error) => {
        toast("Failed to fetch guest tag data", "error");
        console.error(error);
        setLoading(false);
      });
  }, [state, perPage, searchTerm, toast]);

  const handleDeleteTag = () => {
    setLoading(true);
    axios
      .post(
        DELETE_TAG_API,
        { tag_id: state },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        }
      )
      .then((res) => {
        toast("Tag deleted successfully", "success");
        navigate(TAG_MANAGEMENT);
        setLoading(false);
      })
      .catch((err) => {
        toast("Failed to delete tag", "error");
        console.error(err);
        setLoading(false);
      });
  };

  const handlePageClick = (e) => {
    fetchGuestTagTableData(e.selected + 1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchGuestTagTableData();
    }
  };

  const handleClearFilter = () => {
    setSearchTerm("");
    setIsSearchCleared(true);
  };

  useEffect(() => {
    if (state) {
      fetchTagDetails();
      fetchGuestTagTableData();
    }
  }, [state, fetchTagDetails, fetchGuestTagTableData]);

  useEffect(() => {
    if (isSearchCleared && searchTerm === "") {
      fetchGuestTagTableData();
      setIsSearchCleared(false);
    }
  }, [isSearchCleared, searchTerm, fetchGuestTagTableData]);

  return (
    <div className="flex flex-col w-full h-full">
      {showModal && (
        <Modal
          title="Delete Tag"
          confirmation={handleDeleteTag}
          toggleModal={() => {
            setShowModal(!showModal);
          }}
          submitLabel="Remove"
        >
          <div className="flex flex-col w-full items-center justify-center gap-[16px] py-[32px]">
            <Warning2 size="64" color="#ff0000" variant="Bold" />
            <p>Are you sure you want to delete {originalName} tag?</p>
          </div>
        </Modal>
      )}
      <LoadingInComponent isLoading={loading}>
        <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] overflow-y-auto gap-[12px] md:gap-[24px]">
          <Breadcrumb items={breadcrumbItems} />

          {/* Title */}
          <div className="flex items-center justify-between">
            <h1 className="text-[29px]">Edit {originalName}</h1>
            <Can permission="delete_tag">
            <Button
              variant="error"
              onClick={() => {
                setShowModal(!showModal);
              }}
            >
              Delete
            </Button>
            </Can>
          </div>

          {/* Form field */}
          {formData && originalName && (
            <div className="flex flex-col w-full gap-[12px] md:gap-[24px]">
              <div className="flex flex-col md:flex-row gap-[12px] md:gap-[24px]">
                <SectionCard>
                  <div className="grid grid-cols-1 w-full p-6 gap-6">
                    <TextboxSet
                      type="text"
                      name="name"
                      value={formData.name}
                      label="Name"
                      required
                      className="w-full"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          name: e.target.value,
                        })
                      }
                    />
                    <TextboxSet
                      name="color"
                      label="Tag Color"
                      type="color"
                      className="p-0 h-[40px] w-full"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          color: e.target.value,
                        })
                      }
                      value={formData.color}
                    />
                  </div>
                </SectionCard>
                <SectionCard className="md:max-w-[300px]">
                  <TimestampCard
                    created_at={timeStampData.created_at}
                    updated_at={timeStampData.updated_at}
                  />
                </SectionCard>
              </div>
              {/* Save and cancel buttons */}
              <div className="flex items-center gap-3">
                <Button
                  className="w-[100px]"
                  onClick={() => {
                    handleUpdateTag();
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  className="w-[100px]"
                  onClick={() => {
                    navigate(TAG_MANAGEMENT);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Tag Edit Table */}
          {tableData && tableMeta && (
            <TagEditTable
              tagId={state}
              data={tableData}
              meta={tableMeta}
              handlePageClick={handlePageClick}
              searchTerm={searchTerm}
              handleSearch={handleSearch}
              handleKeyDown={handleKeyDown}
              clearFilter={handleClearFilter}
            />
          )}
        </div>
      </LoadingInComponent>
    </div>
  );
};

export default TagsEdit;
